import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import type { 
  Message, 
  TypingIndicator 
} from '@/types';

interface WebSocketConfig {
  url: string;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
  heartbeatIncoming?: number;
  heartbeatOutgoing?: number;
}

interface ConnectionCallbacks {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onReconnect?: (attemptNumber: number) => void;
}

class WebSocketService {
  private client: Client;
  private config: WebSocketConfig;
  private callbacks: ConnectionCallbacks = {};
  private subscriptions: Map<string, any> = new Map();
  private reconnectAttempts = 0;
  private isConnecting = false;
  private authTokenProvider: (() => Promise<string | null>) | null = null;

  constructor(config: WebSocketConfig, callbacks?: ConnectionCallbacks) {
    this.config = {
      reconnectDelay: 5000,
      maxReconnectAttempts: 10,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      ...config
    };
    
    this.callbacks = callbacks || {};
    this.client = this.createClient();
  }

  private createClient(): Client {
    const client = new Client({
      webSocketFactory: () => new SockJS(this.config.url),
      connectHeaders: {},
      heartbeatIncoming: this.config.heartbeatIncoming!,
      heartbeatOutgoing: this.config.heartbeatOutgoing!,
      reconnectDelay: this.config.reconnectDelay!,
    });

    client.onConnect = this.onConnect.bind(this);
    client.onDisconnect = this.onDisconnect.bind(this);
    client.onStompError = this.onError.bind(this);
    client.onWebSocketError = this.onWebSocketError.bind(this);

    return client;
  }

  /**
   * Set authentication token provider for WebSocket connection
   */
  setAuthTokenProvider(provider: () => Promise<string | null>): void {
    this.authTokenProvider = provider;
  }

  private async onConnect(): Promise<void> {
    console.log('WebSocket connected successfully');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Set up authentication header for future requests
    if (this.authTokenProvider) {
      const token = await this.authTokenProvider();
      if (token) {
        this.client.connectHeaders = {
          'Authorization': `Bearer ${token}`
        };
      }
    }

    this.callbacks.onConnect?.();
  }

  private onDisconnect(): void {
    console.log('WebSocket disconnected');
    this.callbacks.onDisconnect?.();
    this.clearSubscriptions();
  }

  private onError(frame: any): void {
    const error = new Error(`STOMP error: ${frame.headers?.message || 'Unknown error'}`);
    console.error('WebSocket STOMP error:', error);
    this.callbacks.onError?.(error);
  }

  private onWebSocketError(event: Event): void {
    const error = new Error(`WebSocket error: ${event.type}`);
    console.error('WebSocket connection error:', error);
    this.callbacks.onError?.(error);
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.client.connected || this.isConnecting) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;

    try {
      // Set auth token before connecting
      if (this.authTokenProvider) {
        const token = await this.authTokenProvider();
        if (token) {
          this.client.connectHeaders = {
            'Authorization': `Bearer ${token}`
          };
        }
      }

      this.client.activate();
    } catch (error) {
      this.isConnecting = false;
      throw new Error(`Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  async disconnect(): Promise<void> {
    if (!this.client.connected) {
      return;
    }

    this.clearSubscriptions();
    await this.client.deactivate();
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.client.connected;
  }

  /**
   * Subscribe to chat messages
   */
  subscribeToChat(chatId: string, onMessage: (message: Message) => void): void {
    if (!this.client.connected) {
      throw new Error('WebSocket not connected');
    }

    const destination = `/topic/chat/${chatId}`;
    
    if (this.subscriptions.has(destination)) {
      console.warn(`Already subscribed to chat ${chatId}`);
      return;
    }

    const subscription = this.client.subscribe(destination, (message) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        onMessage(parsedMessage);
      } catch (error) {
        console.error('Failed to parse chat message:', error);
      }
    });

    this.subscriptions.set(destination, subscription);
    console.log(`Subscribed to chat ${chatId}`);
  }

  /**
   * Unsubscribe from chat messages
   */
  unsubscribeFromChat(chatId: string): void {
    const destination = `/topic/chat/${chatId}`;
    const subscription = this.subscriptions.get(destination);
    
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(destination);
      console.log(`Unsubscribed from chat ${chatId}`);
    }
  }

  /**
   * Subscribe to typing indicators
   */
  subscribeToTyping(chatId: string, onTyping: (indicator: TypingIndicator) => void): void {
    if (!this.client.connected) {
      throw new Error('WebSocket not connected');
    }

    const destination = `/topic/typing/${chatId}`;
    
    if (this.subscriptions.has(destination)) {
      console.warn(`Already subscribed to typing for chat ${chatId}`);
      return;
    }

    const subscription = this.client.subscribe(destination, (message) => {
      try {
        const indicator = JSON.parse(message.body);
        onTyping(indicator);
      } catch (error) {
        console.error('Failed to parse typing indicator:', error);
      }
    });

    this.subscriptions.set(destination, subscription);
  }

  /**
   * Subscribe to user status updates
   */
  subscribeToUserStatus(onStatusUpdate: (user: { userId: string; isOnline: boolean }) => void): void {
    if (!this.client.connected) {
      throw new Error('WebSocket not connected');
    }

    const destination = '/topic/user-status';
    
    if (this.subscriptions.has(destination)) {
      console.warn('Already subscribed to user status updates');
      return;
    }

    const subscription = this.client.subscribe(destination, (message) => {
      try {
        const statusUpdate = JSON.parse(message.body);
        onStatusUpdate(statusUpdate);
      } catch (error) {
        console.error('Failed to parse user status update:', error);
      }
    });

    this.subscriptions.set(destination, subscription);
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator(chatId: string, isTyping: boolean): void {
    if (!this.client.connected) {
      console.warn('Cannot send typing indicator: WebSocket not connected');
      return;
    }

    const message: TypingIndicator = {
      chatId,
      userId: '', // Will be set by server based on auth token
      isTyping
    };

    this.client.publish({
      destination: `/app/typing/${chatId}`,
      body: JSON.stringify(message),
    });
  }

  /**
   * Send message to chat
   */
  sendMessage(chatId: string, content: string, type: string = 'text'): void {
    if (!this.client.connected) {
      throw new Error('WebSocket not connected');
    }

    const message = {
      chatId,
      content,
      type,
      timestamp: new Date().toISOString()
    };

    this.client.publish({
      destination: `/app/chat/${chatId}`,
      body: JSON.stringify(message),
    });
  }

  /**
   * Update user status
   */
  updateUserStatus(status: 'online' | 'away' | 'offline'): void {
    if (!this.client.connected) {
      console.warn('Cannot update user status: WebSocket not connected');
      return;
    }

    this.client.publish({
      destination: '/app/user-status',
      body: JSON.stringify({ status }),
    });
  }

  /**
   * Clear all subscriptions
   */
  private clearSubscriptions(): void {
    this.subscriptions.forEach((subscription) => {
      try {
        subscription.unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    });
    this.subscriptions.clear();
  }

  /**
   * Get connection state
   */
  getConnectionState(): {
    connected: boolean;
    connecting: boolean;
    reconnectAttempts: number;
    subscriptions: string[];
  } {
    return {
      connected: this.client.connected,
      connecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      subscriptions: Array.from(this.subscriptions.keys()),
    };
  }
}

export default WebSocketService;